/**
 * Notifications sagas — async logic, WebSocket event handling.
 */

import { call, put, select, takeEvery, takeLatest, delay } from 'redux-saga/effects';
import * as api from '@/infra/api/client';
import { SYNC_REFRESH } from '@/infra/sync/actions';
import { withSagaFlow, emitSagaPoint } from '@/features/qpl-client/saga-helpers';
import {
  addNotification,
  setNotifications,
  setNotificationsLoading,
  setNotificationsError,
  FETCH_NOTIFICATIONS,
} from './ducks';
import { selectNotifications } from './selectors';
import { Notification } from '@/infra/types/domain';
import { PaginatedResponse } from '@/infra/types/api';

// ─── Constants ────────────────────────────────────────────

const MAX_RETRIES = 3;
const INITIAL_BACKOFF_MS = 1000;

// Transient HTTP status codes worth retrying
const RETRYABLE_STATUS_CODES = [429, 503];

// ─── Helpers ──────────────────────────────────────────────

function isRetryableError(error: any): boolean {
  const status = error?.response?.status;
  return RETRYABLE_STATUS_CODES.includes(status);
}

// ─── Fetch Notifications ───────────────────────────────────

export function* fetchNotificationsSaga(action?: {
  type: string;
  payload?: { page?: number; limit?: number };
}): Generator {
  yield call(withSagaFlow, 'notifications.fetch', ['notifications'], action, function* () {
    yield put(setNotificationsLoading(true));
    const page = action?.payload?.page ?? 1;
    const limit = action?.payload?.limit ?? 50;

    let retries = 0;

    while (retries <= MAX_RETRIES) {
      try {
        const result = (yield call(api.getNotifications, { page, limit })) as PaginatedResponse<Notification>;
        yield* emitSagaPoint('api_getNotifications_ok', { count: result.items.length, retries });

        // Deduplicate: merge HTTP-fetched items with any that arrived via WS
        const existingNotifications = (yield select(selectNotifications)) as Notification[];
        const existingIds = new Set(existingNotifications.map((n) => n.id));
        const newItems = result.items.filter((n) => !existingIds.has(n.id));
        const merged = [...existingNotifications, ...newItems];

        yield put(setNotifications(merged));
        yield put(setNotificationsError(null));
        yield put(setNotificationsLoading(false));
        return;
      } catch (error: any) {
        if (isRetryableError(error) && retries < MAX_RETRIES) {
          const backoff = INITIAL_BACKOFF_MS * Math.pow(2, retries);
          yield* emitSagaPoint('retry_backoff', { retries, backoff_ms: backoff });
          yield delay(backoff);
          retries++;
          continue;
        }

        yield put(setNotificationsError(error.message || 'Failed to fetch notifications'));
        yield put(setNotificationsLoading(false));
        yield* emitSagaPoint('api_getNotifications_err', { message: error?.message, retries });
        return;
      }
    }
  });
}

// ─── WebSocket Event Handlers ──────────────────────────────

function* handleWsNotificationFired(action: {
  type: string;
  payload: Notification;
}): Generator {
  yield put(addNotification(action.payload));
}

// ─── Root Notifications Saga ───────────────────────────────

export function* notificationsSaga(): Generator {
  yield takeLatest(FETCH_NOTIFICATIONS, fetchNotificationsSaga);
  yield takeEvery('WS_NOTIFICATION_FIRED', handleWsNotificationFired);
  yield takeLatest(SYNC_REFRESH, fetchNotificationsSaga);
}
